import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Shield, Plug, Server, CheckCircle2, Eye, EyeOff } from 'lucide-react';

type ProviderName = 'nmi' | 'stripe' | 'payarc';

interface PaymentGatewaysFormProps {
  companyId: string;
}

export function PaymentGatewaysForm({ companyId }: PaymentGatewaysFormProps) {
  const STRIPE_TEST_KEY = 'pk_test_51RBO7SQ2YDdeMSvoEf10pXSKAhiAUNALgvoCwU45DqjKR2LW8Gc4rIHJY4w4xBIidcliZ6y1YDq0xX96S4NOXoFW00OtWGILQJ';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState<{ card?: ProviderName; ach?: 'nmi' | 'payarc' }>({});
  const [stripe, setStripe] = useState<{ enabled: boolean; publishableKey?: string; secretKey?: string }>({ enabled: false });
  const [payarc, setPayarc] = useState<{ enabled: boolean; mode: 'test' | 'live'; apiToken?: string; hostedFieldsAvailable?: boolean }>({ enabled: false, mode: 'test' });
  const [nmiAchEnabled, setNmiAchEnabled] = useState<boolean>(true);
  const [nmiCardEnabled, setNmiCardEnabled] = useState<boolean>(false);
  const [nmi, setNmi] = useState<{ username?: string; tokenizationKey?: string; securityKey?: string }>({});
  const [showNmiToken, setShowNmiToken] = useState(false);
  const [showNmiSecret, setShowNmiSecret] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId));
        if (snap.exists()) {
          const data = snap.data() as any;
          const pg = data.paymentGateways || {};
          setDefaults(pg.defaults || {});
          const stripeCfg = pg.providers?.stripe || {};
          const payarcCfg = pg.providers?.payarc || {};
          const nmiCfg = pg.providers?.nmi || {};
          setStripe({ enabled: !!stripeCfg.enabled, publishableKey: stripeCfg.credentials?.publishableKey, secretKey: stripeCfg.credentials?.secretKey });
          setPayarc({ enabled: !!payarcCfg.enabled, mode: payarcCfg.mode || 'test', apiToken: payarcCfg.credentials?.apiToken, hostedFieldsAvailable: !!payarcCfg.features?.hostedFieldsAvailable });
          setNmiAchEnabled((pg.defaults?.ach || 'nmi') === 'nmi');
          setNmiCardEnabled((pg.defaults?.card || 'nmi') === 'nmi' || !!nmiCfg.enabled);
          // Load wikiPayit (NMI) credentials
          setNmi({
            username: data.wikiPayitSettings?.username || '',
            tokenizationKey: data.wikiPayitSettings?.tokenizationKey || '',
            securityKey: data.wikiPayitSettings?.securityKey || '',
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  // Validate Stripe keys when they change
  const isValidStripeKeys = (publishableKey: string, secretKey: string) => {
    if (!publishableKey || !secretKey) return false;
    const pkValid = publishableKey.startsWith('pk_test_') || publishableKey.startsWith('pk_live_');
    const skValid = secretKey.startsWith('sk_test_') || secretKey.startsWith('sk_live_');
    const sameMode = (publishableKey.startsWith('pk_test_') && secretKey.startsWith('sk_test_')) ||
                    (publishableKey.startsWith('pk_live_') && secretKey.startsWith('sk_live_'));
    return pkValid && skValid && sameMode;
  };

  // Enforce exclusivity: only one card provider enabled at a time among Stripe, PayArc, NMI
  useEffect(() => {
    const enabled = [stripe.enabled ? 'stripe' : null, payarc.enabled ? 'payarc' : null, nmiCardEnabled ? 'nmi' : null].filter(Boolean) as ProviderName[];
    if (enabled.length <= 1) return;
    const currentDefault = defaults.card as ProviderName | undefined;
    // Keep only the current default; disable others
    if (currentDefault === 'stripe') {
      setPayarc((p) => ({ ...p, enabled: false }));
      setNmiCardEnabled(false);
    } else if (currentDefault === 'payarc') {
      setStripe((s) => ({ ...s, enabled: false }));
      setNmiCardEnabled(false);
    } else if (currentDefault === 'nmi') {
      setStripe((s) => ({ ...s, enabled: false }));
      setPayarc((p) => ({ ...p, enabled: false }));
    } else {
      // No default; prefer Stripe if set, else PayArc, else NMI
      if (stripe.enabled) {
        setDefaults((d) => ({ ...d, card: 'stripe' }));
        setPayarc((p) => ({ ...p, enabled: false }));
        setNmiCardEnabled(false);
      } else if (payarc.enabled) {
        setDefaults((d) => ({ ...d, card: 'payarc' }));
        setStripe((s) => ({ ...s, enabled: false }));
        setNmiCardEnabled(false);
      } else if (nmiCardEnabled) {
        setDefaults((d) => ({ ...d, card: 'nmi' }));
        setStripe((s) => ({ ...s, enabled: false }));
        setPayarc((p) => ({ ...p, enabled: false }));
      }
    }
  }, [stripe.enabled, payarc.enabled, nmiCardEnabled, defaults.card]);

  const save = async () => {
    setSaving(true);
    try {
      // Validate Stripe keys if Stripe is enabled
      if (stripe.enabled && !isValidStripeKeys(stripe.publishableKey || '', stripe.secretKey || '')) {
        throw new Error('Both valid Stripe publishable key and secret key are required to enable Stripe');
      }

      // Determine default card based on enabled toggles
      let defaultCard: ProviderName = defaults.card || 'nmi';
      if (stripe.enabled) defaultCard = 'stripe';
      if (payarc.enabled) defaultCard = 'payarc';
      if (nmiCardEnabled) defaultCard = 'nmi';
      if (!stripe.enabled && !payarc.enabled) defaultCard = 'nmi';

      // Determine default ACH based on NMI ACH toggle
      const defaultAch: 'nmi' | 'payarc' = nmiAchEnabled ? 'nmi' : 'payarc';

      // Determine Stripe mode based on keys
      const stripeMode = stripe.publishableKey?.startsWith('pk_test_') ? 'test' : 'live';

      const paymentGateways = {
        defaults: {
          card: defaultCard,
          ach: defaultAch,
        },
        providers: {
          stripe: {
            enabled: stripe.enabled,
            mode: stripeMode,
            credentials: { publishableKey: stripe.publishableKey || '', secretKey: stripe.secretKey || '' },
          },
          payarc: {
            enabled: payarc.enabled,
            mode: payarc.mode,
            credentials: { apiToken: payarc.apiToken || '' },
            features: { hostedFieldsAvailable: !!payarc.hostedFieldsAvailable },
          },
          nmi: { enabled: nmiCardEnabled, mode: 'live', credentials: {} },
        },
      };
      await updateDoc(doc(db, 'companies', companyId), { paymentGateways, wikiPayitSettings: { ...nmi } });
    } catch (error) {
      console.error('Error saving payment gateways:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const CardHeaderRow = ({ icon, title, subtitle, right }: { icon: React.ReactNode; title: string; subtitle: string; right?: React.ReactNode }) => (
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          {icon}
        </div>
        <div>
          <CardTitle className="leading-tight">{title}</CardTitle>
          <CardDescription>{subtitle}</CardDescription>
        </div>
      </div>
      {right}
    </div>
  );

  const DefaultBadge = ({ active }: { active: boolean }) => (
    <Badge
      variant="outline"
      className={`ml-2 rounded-full px-3 py-1 text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-transparent text-muted-foreground border-muted-foreground'
      }`}
    >
      {active && <CheckCircle2 className="h-3.5 w-3.5" />}
      {active ? 'Default (Card)' : 'Not Default'}
    </Badge>
  );

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border">
        <CardHeader>
          <CardTitle>Payment Gateways</CardTitle>
          <CardDescription>Enable exactly one card gateway (Stripe or PayArc). You can enable NMI for ACH alongside a card gateway.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Stripe Card */}
            <Card className={`border ${stripe.enabled ? 'ring-1 ring-primary/40 shadow-md' : 'shadow-sm'}`}>
              <CardHeader>
                <CardHeaderRow
                  icon={<CreditCard className="h-5 w-5" />}
                  title="Stripe"
                  subtitle="Card payments via Stripe"
                  right={<div className="flex items-center gap-2">
                    {(((stripe.publishableKey || '').startsWith('pk_test'))
                      || ((stripe.secretKey || '').startsWith('sk_test'))
                      || stripe.publishableKey === STRIPE_TEST_KEY
                      || stripe.secretKey === STRIPE_TEST_KEY) && (
                        <Badge
                          variant="outline"
                          className="bg-amber-50 text-amber-700 border-amber-200 text-center justify-center px-3 py-1 leading-tight"
                        >
                          Test Keys
                        </Badge>
                      )}
                    {defaults.card === 'stripe' && <DefaultBadge active />}
                    <Switch
                      checked={stripe.enabled}
                      onCheckedChange={(v) => {
                        const next = !!v;
                        if (next && !isValidStripeKeys(stripe.publishableKey || '', stripe.secretKey || '')) {
                          // Don't allow enabling without valid keys
                          return;
                        }
                        setStripe((s) => ({ ...s, enabled: next }));
                        if (next) {
                          // Turn off PayArc and set default to Stripe
                          setPayarc((p) => ({ ...p, enabled: false }));
                          setDefaults((d) => ({ ...d, card: 'stripe' }));
                        } else if (defaults.card === 'stripe') {
                          // Fallback default to NMI if Stripe was default
                          setDefaults((d) => ({ ...d, card: payarc.enabled ? 'payarc' : 'nmi' }));
                        }
                      }}
                    />
                  </div>}
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>Publishable Key</Label>
                    <Input 
                      value={stripe.publishableKey || ''} 
                      onChange={(e) => {
                        const newPk = e.target.value;
                        setStripe((s) => ({ 
                          ...s, 
                          publishableKey: newPk,
                          // Auto-disable if keys become invalid
                          enabled: s.enabled && isValidStripeKeys(newPk, s.secretKey || '')
                        }));
                      }} 
                      placeholder="pk_live_... or pk_test_..." 
                      className={(!stripe.publishableKey || !stripe.publishableKey.match(/^pk_(live|test)_/)) ? 'border-red-300' : ''}
                    />
                  </div>
                  <div>
                    <Label>Secret Key</Label>
                    <Input 
                      type="password" 
                      value={stripe.secretKey || ''} 
                      onChange={(e) => {
                        const newSk = e.target.value;
                        setStripe((s) => ({ 
                          ...s, 
                          secretKey: newSk,
                          // Auto-disable if keys become invalid
                          enabled: s.enabled && isValidStripeKeys(s.publishableKey || '', newSk)
                        }));
                      }} 
                      placeholder="sk_live_... or sk_test_..." 
                      className={(!stripe.secretKey || !stripe.secretKey.match(/^sk_(live|test)_/)) ? 'border-red-300' : ''}
                    />
                  </div>
                  {!isValidStripeKeys(stripe.publishableKey || '', stripe.secretKey || '') && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-800 font-medium mb-1">Invalid Keys</p>
                      <p className="text-xs text-red-600">Both keys are required and must be either both test keys (pk_test_/sk_test_) or both live keys (pk_live_/sk_live_)</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <div className="text-xs text-muted-foreground">
                      {stripe.publishableKey?.startsWith('pk_test_') ? 'Test Mode' : stripe.publishableKey?.startsWith('pk_live_') ? 'Live Mode' : 'Enter valid keys'}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const TEST_PK = 'pk_test_51RBO7SQ2YDdeMSvoEf10pXSKAhiAUNALgvoCwU45DqjKR2LW8Gc4rIHJY4w4xBIidcliZ6y1YDq0xX96S4NOXoFW00OtWGILQJ';
                        const TEST_SK = 'sk_test_51RBO7SQ2YDdeMSvoEf10pXSKAhiAUNALgvoCwU45DqjKR2LW8Gc4rIHJY4w4xBIidcliZ6y1YDq0xX96S4NOXoFW00OtWGILQJ';
                        setStripe((s) => ({ ...s, publishableKey: TEST_PK, secretKey: TEST_SK }));
                      }}
                    >
                      Load Test Keys
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WikiPayit (NMI) Card */}
            <Card className={`border ${nmiCardEnabled ? 'ring-1 ring-primary/40 shadow-md' : 'shadow-sm'}`}>
              <CardHeader>
                <CardHeaderRow
                  icon={<Shield className="h-5 w-5" />}
                  title="WikiPayit (NMI)"
                  subtitle="Card payments via NMI"
                  right={<div className="flex items-center gap-2">
                    {defaults.card === 'nmi' && <DefaultBadge active />}
                    <Switch
                      checked={nmiCardEnabled}
                      onCheckedChange={(v) => {
                        const next = !!v;
                        setNmiCardEnabled(next);
                        if (next) {
                          // Turn off Stripe/PayArc and set default to NMI
                          setStripe((s) => ({ ...s, enabled: false }));
                          setPayarc((p) => ({ ...p, enabled: false }));
                          setDefaults((d) => ({ ...d, card: 'nmi' }));
                        } else if (defaults.card === 'nmi') {
                          // Fallback default
                          setDefaults((d) => ({ ...d, card: stripe.enabled ? 'stripe' : (payarc.enabled ? 'payarc' : 'nmi') }));
                        }
                      }}
                    />
                  </div>}
                />
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Account Username</Label>
                  <Input value={nmi.username || ''} onChange={(e) => setNmi((s) => ({ ...s, username: e.target.value }))} placeholder="Enter NMI username" />
                </div>
                <div className="relative">
                  <Label>Tokenization Key</Label>
                  <Input type={showNmiToken ? 'text' : 'password'} value={nmi.tokenizationKey || ''} onChange={(e) => setNmi((s) => ({ ...s, tokenizationKey: e.target.value }))} placeholder="••••••••••••••" />
                  <Button variant="ghost" size="icon" className="absolute right-1 top-6 h-7 w-7" onClick={() => setShowNmiToken((v) => !v)}>
                    {showNmiToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="relative">
                  <Label>Security Key (API)</Label>
                  <Input type={showNmiSecret ? 'text' : 'password'} value={nmi.securityKey || ''} onChange={(e) => setNmi((s) => ({ ...s, securityKey: e.target.value }))} placeholder="••••••••••••••" />
                  <Button variant="ghost" size="icon" className="absolute right-1 top-6 h-7 w-7" onClick={() => setShowNmiSecret((v) => !v)}>
                    {showNmiSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* PayArc Card */}
            <Card className={`border ${payarc.enabled ? 'ring-1 ring-primary/40 shadow-md' : 'shadow-sm'}`}>
              <CardHeader>
                <CardHeaderRow
                  icon={<Server className="h-5 w-5" />}
                  title="PayArc"
                  subtitle="Card payments via PayArc"
                  right={<div className="flex items-center gap-2">
                    {defaults.card === 'payarc' && <DefaultBadge active />}
                    <Switch
                      checked={payarc.enabled}
                      onCheckedChange={(v) => {
                        const next = !!v;
                        setPayarc((p) => ({ ...p, enabled: next }));
                        if (next) {
                          // Turn off Stripe and set default to PayArc
                          setStripe((s) => ({ ...s, enabled: false }));
                          setDefaults((d) => ({ ...d, card: 'payarc' }));
                        } else if (defaults.card === 'payarc') {
                          // Fallback default to NMI if PayArc was default
                          setDefaults((d) => ({ ...d, card: stripe.enabled ? 'stripe' : 'nmi' }));
                        }
                      }}
                    />
                  </div>}
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>Mode</Label>
                    <select className="border rounded p-2 w-full" value={payarc.mode} onChange={(e) => setPayarc((p) => ({ ...p, mode: e.target.value as any }))}>
                      <option value="test">Sandbox/Test</option>
                      <option value="live">Live</option>
                    </select>
                  </div>
                  <div>
                    <Label>API Bearer Token</Label>
                    <Input type="password" value={payarc.apiToken || ''} onChange={(e) => setPayarc((p) => ({ ...p, apiToken: e.target.value }))} placeholder="••••••••" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!payarc.hostedFieldsAvailable} onCheckedChange={(v) => setPayarc((s) => ({ ...s, hostedFieldsAvailable: !!v }))} />
                    <Label>Hosted Fields Available</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* NMI ACH Card */}
            <Card className={`border ${nmiAchEnabled ? 'ring-1 ring-primary/40 shadow-md' : 'shadow-sm'}`}>
              <CardHeader>
                <CardHeaderRow
                  icon={<Plug className="h-5 w-5" />}
                  title="NMI (ACH)"
                  subtitle="Enable bank (ACH) payments via NMI"
                  right={<div className="flex items-center gap-2">
                    {nmiAchEnabled && <Badge className="bg-emerald-600 text-white">Enabled</Badge>}
                    <Switch
                      checked={nmiAchEnabled}
                      onCheckedChange={(v) => {
                        const next = !!v;
                        setNmiAchEnabled(next);
                        setDefaults((d) => ({ ...d, ach: next ? 'nmi' : 'payarc' }));
                      }}
                    />
                  </div>}
                />
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground">
                  ACH can be enabled alongside a card gateway. When enabled, defaults to NMI for ACH.
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={save} disabled={saving} className="gap-2">
              {saving && <CheckCircle2 className="h-4 w-4 animate-pulse" />}
              {saving ? 'Saving…' : 'Save Gateway Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
